import React from 'react';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { FormField } from '../../../../global/form/form-field/form-field';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import { NUMBERS_ONLY_REG_EXP, ONLY_SPACES_REG_EXP } from '../../../../../constants/regex.constants';
import OptionsButton from '../../../../global/options-button/options-button';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import Text from '../../../../global/text/text';
import DefaultButton from '../../../../global/default-button/default-button';
import { MultiSelectButton } from '../../../../global/multi-select/multi-select-button';
import { OrganizationFormVM } from './organization-form.viewmodel';
import { OrganizationLink } from './organization-presenter-participant-link/organization-link.input';
import { addText, cancelText, MORE_BUTTON_TEXT, REMOVE_TEXT, submitText } from '../../../../../constants/common-labels.constants';


const OrganizationFormPresentation = injectIntl(mobxReact.observer((props: { vm: OrganizationFormVM; intl: IntlShape }) => {
    const { intl } = props;
    const formData = props.vm.getOrganizationFormDataVM();
    const { formSubmitAction, isSaving, deleteOrganization, deleteOrganizationJob } = props.vm;

    const removeOnlySpacesFromField = mobx.action((value: string, key: string) => {
        if (!(value.replace(ONLY_SPACES_REG_EXP, '').length)) {
            formData[key] = '';
        }
    });

    const organizationOptions = [
        {
            label: intl.formatMessage(
                {
                    id: 'oe.organization.deleteOrganization',
                    description: 'Delete Organization in sub-window',
                    defaultMessage: 'Delete Organization',
                }
            ),
            action: deleteOrganization,
            danger: true,
            disabled: !deleteOrganizationJob.triggerEnabled,
        }
    ];
    if (!formData) {
        return <CommonPopupBodyFooterLayout
            body={
                <div className='oe-form meeting-form'>
                    <FormattedMessage
                        description='Label text'
                        defaultMessage='Loading organization data{suffix}'
                        id='oe.organization.loadingData'
                        values={{
                            suffix: '...'
                        }}
                    />
                </div>
            }
        />;
    }

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form' >
                    <div className='oe-form__body'>
                        <FormField label={<FormattedMessage
                            defaultMessage='Organization'
                            id='oe.event.organization'
                            description='Event Form label'
                        />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.name || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.name = value;
                                    formData.setServerErrorMessage('');
                                })}
                                onBlur={mobx.action(({ target: { value } }) => {
                                    removeOnlySpacesFromField(value, 'name');
                                })}
                                autoFocus={!props.vm.getOrganizationId()}
                            />
                            {formData.formChecked &&
                                <span className='oe-form-field__error'>{formData.getNameError()}</span>}
                            {formData.getServerErrorMessage() && <div className='oe-form-field__error'>{formData.getServerErrorMessage()}</div>}

                        </FormField>
                        <FormField label={<FormattedMessage
                            defaultMessage='Type'
                            id='oe.event.type'
                            description='Event Form label'
                        />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.organizationType || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.organizationType = value;
                                    formData.setServerErrorMessage('');
                                })}
                                onBlur={mobx.action(({ target: { value } }) => {
                                    removeOnlySpacesFromField(value, 'organizationType');
                                })}
                            />
                            {formData.formChecked &&
                                <span className='oe-form-field__error'>{formData.getFieldError('organizationType')}</span>}
                        </FormField>
                        <FormField label={<FormattedMessage
                            defaultMessage='Ticker'
                            id='oe.event.ticker'
                            description='Event Form label'
                        />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.organizationTicker || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.organizationTicker = value;
                                    formData.setServerErrorMessage('');
                                })}
                                onBlur={mobx.action(({ target: { value } }) => {
                                    removeOnlySpacesFromField(value, 'organizationTicker');
                                })}
                            />
                            {formData.formChecked &&
                                <span className='oe-form-field__error'>{formData.getFieldError('organizationTicker')}</span>}
                        </FormField>
                        <FormField label={<FormattedMessage
                            defaultMessage='External Organization ID'
                            id='oe.event.externalOrganizationId'
                            description='Event Form label'
                        />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.externalOrganizationId || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.externalOrganizationId = value;
                                    formData.setServerErrorMessage('');
                                })}
                                onBlur={mobx.action(({ target: { value } }) => {
                                    removeOnlySpacesFromField(value, 'externalOrganizationId');
                                })}
                            />
                            {formData.formChecked &&
                                <span className='oe-form-field__error'>
                                    {formData.getFieldError('externalOrganizationId')}
                                </span>}
                        </FormField>
                        <FormField
                            label={<FormattedMessage
                                id='oe.organization.priorityInEvent'
                                description='Label text'
                                defaultMessage='Priority in Event'
                            />}
                        >
                            <input
                                className='oe-form-field__control'
                                value={formData.priorityInEvent || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    if (value === '' || NUMBERS_ONLY_REG_EXP.test(value)) {
                                        formData.priorityInEvent = +value;
                                    }
                                })}
                            />
                            {formData.formChecked &&
                                <span className='oe-form-field__error'>
                                    {formData.getPriorityInEventError()}
                                </span>}
                        </FormField>
                        <div className='event_form__items-section'>
                            <div className='event_form__items-section__title'>
                                <FormattedMessage
                                    id='oe.common.chaperone'
                                    description='Label text'
                                    defaultMessage='Chaperone'
                                />
                            </div>
                            <div className='event_form__items-section__selected-items'>
                                {formData.chaperones
                                    .slice()
                                    .sort((a, b) => a.firstName.localeCompare(b.firstName))
                                    .map(({ username, firstName, lastName }) => (
                                        <div
                                            key={username}
                                            className='event_form__items-section__item-wrapper'
                                        >
                                            <Text
                                                className='event_form__items-section__item-label'
                                                message={`${firstName} ${lastName}`}
                                                withEllipsis
                                            />
                                            <DefaultButton
                                                text={<FormattedMessage {...REMOVE_TEXT} />}
                                                modifier='remove'
                                                onClick={() => formData.removeChaperone(username)}
                                                className='event_form__items-section__remove-item-button'
                                            />
                                        </div>
                                    ))}
                            </div>
                            <MultiSelectButton
                                className='event_form__items-section__add-button'
                                items={props.vm.usersList}
                                disabled={formData.chaperones || []}
                                value={formData.chaperones || []}
                                keyProperty='username'
                                nameProperty='name'
                                text={
                                    <FormattedMessage
                                        id='oe.organizations.addChaperone'
                                        description='Button text'
                                        defaultMessage='{prefix}Add Chaperone'
                                        values={{
                                            prefix: '+ '
                                        }}
                                    />
                                }
                                applyButtonText={addText()}
                                resetButtonText={cancelText()}
                                onChange={formData.setChaperones}
                            />
                        </div>
                        {
                            formData.presenterLink &&
                            <OrganizationLink.Presenter vm={formData.presenterLink} onCopy={() => formData.setIsPresenterLinkCopied(true)} />
                        }
                        {
                            formData.participantLink &&
                            <OrganizationLink.Participant vm={formData.participantLink} onCopy={() => formData.setIsParticipantLinkCopied(true)} />
                        }
                        <FormField label={<FormattedMessage
                            defaultMessage='Approved Domains'
                            id='oe.event.approvedDomains'
                            description='Input label'
                        />}>
                            <textarea
                                className='oe-form-field__control'
                                value={formData.approvedDomains || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.approvedDomains = value;
                                    formData.setServerErrorMessage('');
                                })}
                                onBlur={mobx.action(({ target: { value } }) => {
                                    removeOnlySpacesFromField(value, 'approvedDomains');
                                    formData.getApprovedDomainsError();
                                })}
                            />
                            {formData.getFieldValidationError('approvedDomains') &&
                                <span className='oe-form-field__error'>{formData.getFieldValidationError('approvedDomains')}</span>}
                            <p className='oe-form-field__info'>
                                <FormattedMessage
                                    id='oe.organization.approvedDomainInfo'
                                    description='Input info text'
                                    defaultMessage={'Anyone with an email address ending in a domain on the Approved List ' +
                                        'will be able to request and automatically receive an Access Code for the event.'}
                                />
                            </p>
                            <span className='oe-form-field__info'>
                                <FormattedMessage
                                    id='oe.organization.approvedDomainRequirements'
                                    description='Input info text'
                                    defaultMessage='Please enter email domains without the @ symbol, eg openexc.com.  Enter one domain per line.'
                                />
                            </span>
                        </FormField>
                    </div>
                </form>
            }
            footer={
                <>
                    {isSaving && <span className='oe-popup-layout__footer-status-area'>
                        <FormattedMessage
                            id='oe.organization.organizationDataIsLoading'
                            description='Preloader message text'
                            defaultMessage='Organization data is loading, please wait{suffix}'
                            values={{
                                suffix: '...'
                            }}
                        />
                    </span>}
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={submitText(!!props.vm.getOrganizationId())}
                            modifier={['fat', 'confirm-safe']}
                            onClick={formSubmitAction}
                            disabled={isSaving}
                        />
                        <DefaultCloseSubWindowButton text='Cancel' />
                        {props.vm.getOrganizationId() && hasPermission(Permission.DELETE_ORGANIZATION) && <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                            options={organizationOptions}
                            position='top'
                            align='start'
                        />}
                    </div>
                </>}
        />

    );
}));

export { OrganizationFormPresentation };
